import { Injectable, inject } from '@angular/core';
import { AGENT_PROJECTION_SIGNAL_IDS, DomainSignalId, type DomainSignalChange } from '@ag-common-lib/public-api';
import { Observable, debounceTime, filter, map, merge } from 'rxjs';
import { DomainSignalsService } from '../domain-signals.service';

/**
 * How long a burst of edits is allowed to settle before a list reloads.
 *
 * A single save is several row events across several tables — the agent row, then its
 * emails, then its phones — and each is its own signal. Reloading on the first would
 * refetch two more times for one save.
 */
const AGENT_LIST_RELOAD_DEBOUNCE_MS = 3_000;

/**
 * The same burst, seen from a screen showing one agent.
 *
 * Shorter than the list's window because an open editor should feel immediate, but not
 * absent: without it, saving one contact tab refetched the same agent three times — once
 * for the agent row, once for its phones, once for its addresses. Short enough to read as
 * instant, long enough to collapse one save into one read.
 */
const AGENT_RELOAD_DEBOUNCE_MS = 400;

/**
 * When agent data has moved, and which agents moved with it.
 *
 * Backend-independent by design: the signals describe the projection changing, not who
 * is reading it, so a screen wired to these refreshes correctly whichever implementation
 * `AgentSearchService` resolved to.
 *
 * The two streams answer different questions on purpose. A list cannot usefully act on
 * "agent X changed" — X may not be on the page, and deciding that requires tracking
 * rendered keys — so it takes the debounced any-change signal. A screen showing one agent
 * can act on it precisely, and should: reloading an open editor because somebody else was
 * edited is the churn this exists to avoid.
 */
@Injectable({ providedIn: 'root' })
export class AgentChangesService {
  private readonly domainSignals = inject(DomainSignalsService);

  private readonly agentProjectionChanges$: Observable<DomainSignalChange> = merge(
    ...AGENT_PROJECTION_SIGNAL_IDS.map(signalId => this.domainSignals.onChangedRows(signalId)),
  );

  /**
   * Any change to an agent or to an agency, debounced.
   *
   * Agencies are included because the resolved MGA and agency names are joined onto every
   * agent row: a rename changes what a list shows without touching a single agent.
   */
  readonly agentsChanged$: Observable<void> = merge(
    this.agentProjectionChanges$,
    this.domainSignals.onChangedRows(DomainSignalId.agencies),
  ).pipe(
    debounceTime(AGENT_LIST_RELOAD_DEBOUNCE_MS),
    map(() => undefined),
  );

  /**
   * Emits when this agent specifically changed.
   *
   * A coarse change also emits: it means the signal could not name every row it covered —
   * a backfill, or a client that missed a publish — so "not in the list" stops being
   * evidence that this agent is untouched.
   */
  agentChanged$(agentDbId: string): Observable<void> {
    return this.agentProjectionChanges$.pipe(
      filter(change => change.isCoarse || change.changedRowKeys.includes(agentDbId)),
      debounceTime(AGENT_RELOAD_DEBOUNCE_MS),
      map(() => undefined),
    );
  }
}
